const CSS_ERROR = "error";
const CLIENT_ERROR_SUFFIX = "-client-error";

const clientErrorId = (field) =>
  `${field.id || field.name}${CLIENT_ERROR_SUFFIX}`;

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function getErrorMessage(field, messages) {
  if (field.validity.valueMissing) return messages.required;
  if (field.validity.rangeUnderflow) return messages.rangeUnderflow;
  return messages.invalid;
}

function getOrCreateErrorSpan(container, errorId) {
  const existing = document.getElementById(errorId);
  if (existing) return existing;

  const span = document.createElement("span");
  span.id = errorId;
  span.className = "error-message";
  container.appendChild(span);
  return span;
}

function showFieldError(field, messages) {
  const container = field.closest(".form-field") ?? field.closest("fieldset");
  if (!container) return;

  container.classList.add(CSS_ERROR);

  if (field.type === "date" && !field.validity.valid) {
    field.classList.add(CSS_ERROR);
  }

  const errorId = clientErrorId(field);
  getOrCreateErrorSpan(container, errorId).textContent = getErrorMessage(
    field,
    messages,
  );

  field.setAttribute("aria-invalid", "true");
  field.setAttribute("aria-describedby", errorId);
}

function clearFieldError(field) {
  const container = field.closest(".form-field") ?? field.closest("fieldset");
  if (!container || container.querySelector(":invalid")) return;

  container.classList.remove(CSS_ERROR);
  field.classList.remove(CSS_ERROR);

  document.getElementById(clientErrorId(field))?.remove();

  container.querySelectorAll("[aria-describedby]").forEach((el) => {
    document.getElementById(el.getAttribute("aria-describedby"))?.remove();
    el.removeAttribute("aria-describedby");
    el.removeAttribute("aria-invalid");
  });

  field.removeAttribute("aria-invalid");
  field.removeAttribute("aria-describedby");
}

const LABEL_BY_TYPE = {
  withdrawal: (btn) => btn.dataset.labelWithdrawal,
  cancellation: (btn) => btn.dataset.labelCancellation,
  "extraordinary-cancellation": (btn) => btn.dataset.labelCancellation,
};

function updateSubmitLabel(typeSelect, submitBtn) {
  const resolve = LABEL_BY_TYPE[typeSelect.value];
  submitBtn.textContent = resolve
    ? resolve(submitBtn)
    : submitBtn.dataset.labelDefault;
}

function initLabelSync(_form, typeSelect, submitBtn) {
  const sync = () => updateSubmitLabel(typeSelect, submitBtn);

  requestAnimationFrame(sync);
  window.addEventListener("pageshow", sync);
  typeSelect.addEventListener("change", sync);
}

function initDisabledSync(form, submitBtn) {
  const requiredFields = [
    ...form.querySelectorAll(
      "input[required], select[required], textarea[required]",
    ),
  ];

  function sync() {
    const allFilled = requiredFields.every((field) => {
      if (field.type === "radio") {
        return form.querySelector(`input[name="${field.name}"]:checked`);
      }
      return field.value.trim() !== "";
    });
    submitBtn.disabled = !allFilled;
  }

  requiredFields.forEach((field) => {
    field.addEventListener("input", sync);
    field.addEventListener("change", sync);
  });

  sync();
  requestAnimationFrame(sync);
  window.addEventListener("pageshow", sync);
}

function initSubmitButton(form) {
  const typeSelect = form.querySelector("select[name='type']");
  const submitBtn = form.querySelector("button[type='submit']");
  if (!typeSelect || !submitBtn) return null;

  initLabelSync(form, typeSelect, submitBtn);
  initDisabledSync(form, submitBtn);

  return typeSelect;
}

function initDateSync(form) {
  const pickDateInput = form.querySelector("#pick-date-input");
  const pickDateRadio = form.querySelector("#pick-date-radio");

  const syncRequired = () => {
    if (pickDateInput) pickDateInput.required = pickDateRadio?.checked ?? false;
  };

  syncRequired();

  form.querySelectorAll("input[name='time']").forEach((radio) => {
    radio.addEventListener("change", () => {
      syncRequired();
      if (pickDateInput && !pickDateInput.required) {
        clearFieldError(pickDateInput);
      }
    });
  });

  pickDateInput?.addEventListener("input", () => {
    if (pickDateInput.validity?.valid) clearFieldError(pickDateInput);
  });

  return pickDateInput;
}

function initLiveValidation(form, skipFields) {
  const skip = new Set(skipFields);

  form
    .querySelectorAll("input[required], select[required], textarea[required]")
    .forEach((field) => {
      if (skip.has(field)) return;
      field.addEventListener("input", () => {
        if (field.validity?.valid) clearFieldError(field);
      });
    });
}

function initSubmitHandler(form, messages) {
  form.addEventListener("submit", (e) => {
    const invalid = [
      ...form.querySelectorAll(
        "input:invalid, select:invalid, textarea:invalid",
      ),
    ];
    if (invalid.length === 0) return;

    e.preventDefault();
    invalid.forEach((field) => {
      showFieldError(field, messages);
    });
    invalid[0].scrollIntoView({
      behavior: prefersReducedMotion() ? "auto" : "smooth",
      block: "center",
    });
  });
}

function initCancellationValidation() {
  const form = document.querySelector(".cancellation-and-withdrawal-form");
  if (!form) return;

  const messages = {
    required: form.dataset.errorRequired,
    invalid: form.dataset.errorInvalid,
    rangeUnderflow: form.dataset.errorRangeUnderflow,
  };

  const typeSelect = initSubmitButton(form);
  const pickDateInput = initDateSync(form);

  typeSelect?.addEventListener("change", () => {
    if (typeSelect.validity?.valid) clearFieldError(typeSelect);
  });

  initLiveValidation(form, [typeSelect, pickDateInput].filter(Boolean));
  initSubmitHandler(form, messages);
}

initCancellationValidation();
